import { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { chartGridColor, chartTextColor, chartTooltipTheme } from '../../utils/themeTokens';

Chart.register(...registerables);
Chart.defaults.animation = false;
Chart.defaults.responsive = true;
Chart.defaults.maintainAspectRatio = false;
Chart.defaults.devicePixelRatio = Math.max(2, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

const tc = () => chartTextColor();
const gc = () => chartGridColor();
const ttBase = () => chartTooltipTheme();

function fmtN(v) {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e7) return (v / 1e7).toFixed(2) + 'Cr';
  if (a >= 1e5) return (v / 1e5).toFixed(2) + 'L';
  if (a >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return typeof v === 'number' ? v.toLocaleString('en-IN') : v;
}

const candlePlugin = {
  id: 'eodOhlcCandles',
  afterDatasetsDraw(chart) {
    const ds = chart.data.datasets[0];
    const ohlc = ds?._ohlc;
    if (!ohlc?.length) return;
    const { ctx, chartArea: A, scales: { x, y } } = chart;
    if (!A) return;
    ohlc.forEach((bar, i) => {
      const px = x.getPixelForValue(i);
      const { o, h, l, c } = bar;
      const yO = y.getPixelForValue(o);
      const yH = y.getPixelForValue(h);
      const yL = y.getPixelForValue(l);
      const yC = y.getPixelForValue(c);
      const bullish = c >= o;
      const color = bullish ? '#26a69a' : '#ef5350';
      const halfW = Math.max((A.right - A.left) / ohlc.length * 0.25, 2);
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.moveTo(px, yH);
      ctx.lineTo(px, yL);
      ctx.stroke();
      const bodyTop = Math.min(yO, yC);
      const bodyH = Math.max(Math.abs(yC - yO), 1);
      ctx.fillStyle = bullish ? 'rgba(38,166,154,0.85)' : 'rgba(239,83,80,0.85)';
      ctx.fillRect(px - halfW, bodyTop, halfW * 2, bodyH);
      ctx.strokeStyle = color;
      ctx.strokeRect(px - halfW, bodyTop, halfW * 2, bodyH);
      ctx.restore();
    });
  },
};

export default function EodOCCharts({ series, trendDays, onTrendDaysChange }) {
  const ociRef = useRef(null);
  const mpRef = useRef(null);
  const spotRef = useRef(null);
  const decRef = useRef(null);
  const chartsRef = useRef([]);

  useEffect(() => {
    chartsRef.current.forEach(c => { try { c.destroy(); } catch (_) {} });
    chartsRef.current = [];
    if (!series) return;

    const { oci, cepeOI, mp, dec, ohlc } = series;

    if (ociRef.current && oci?.length) {
      const vals = oci.map(x => x.oci);
      const am = Math.max(Math.abs(Math.min(...vals, 0)), Math.abs(Math.max(...vals, 0)));
      const sm = Math.ceil(am / 100000) * 100000 + 100000;
      const ceOIVals = (cepeOI || []).map(x => x.ceOI);
      const peOIVals = (cepeOI || []).map(x => x.peOI);
      const allOI = [...ceOIVals, ...peOIVals].filter(v => v > 0);
      const oiMin = allOI.length ? Math.min(...allOI) : 0;
      const oiMax = allOI.length ? Math.max(...allOI) : 1;
      const oiRange = (oiMax - oiMin) || 1;

      chartsRef.current.push(new Chart(ociRef.current, {
        type: 'bar',
        data: {
          labels: oci.map(x => x.time),
          datasets: [
            {
              type: 'bar', label: 'OCI', data: vals, yAxisID: 'y',
              backgroundColor: vals.map(v => v >= 0 ? 'rgba(38,166,154,0.45)' : 'rgba(239,83,80,0.45)'),
              borderWidth: 0, order: 2,
            },
            {
              type: 'line', label: 'CE OI', data: ceOIVals, yAxisID: 'y1',
              borderColor: '#ef5350', borderWidth: 1.8, tension: 0.1, pointRadius: 0, fill: false, order: 1,
            },
            {
              type: 'line', label: 'PE OI', data: peOIVals, yAxisID: 'y1',
              borderColor: '#26a69a', borderWidth: 1.8, tension: 0.1, pointRadius: 0, fill: false, order: 1,
            },
          ],
        },
        options: {
          plugins: {
            legend: { display: false },
            tooltip: {
              ...ttBase,
              callbacks: {
                label(ctx) {
                  const v = ctx.raw;
                  if (ctx.datasetIndex === 0) return `OCI: ${fmtN(v)}`;
                  if (ctx.datasetIndex === 1) return `CE OI: ${fmtN(v)}`;
                  return `PE OI: ${fmtN(v)}`;
                },
              },
            },
          },
          interaction: { mode: 'index', intersect: false },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: {
              position: 'left', min: -sm, max: sm,
              ticks: { color: tc(), font: { size: 10 }, callback: v => fmtN(v) },
              grid: { color: gc(), lineWidth: 1, tickLength: 0 },
              title: { display: true, text: 'OCI', color: tc(), font: { size: 9 } },
            },
            y1: {
              position: 'right',
              min: Math.floor(oiMin - oiRange * 0.08),
              max: Math.ceil(oiMax + oiRange * 0.08),
              ticks: { color: tc(), font: { size: 10 }, callback: v => fmtN(v) },
              grid: { drawOnChartArea: false },
              title: { display: true, text: 'OI', color: tc(), font: { size: 9 } },
            },
          },
        },
      }));
    }

    if (mpRef.current && mp?.length) {
      const sv = mp.map(x => x.spot);
      const mv = mp.map(x => x.maxpain);
      const pv = mp.map(x => x.pcr);
      const ap = [...sv.filter(v => v > 0), ...mv.filter(v => v > 0)];
      const pMn = Math.min(...ap);
      const pMx = Math.max(...ap);
      const pR = (pMx - pMn) || 1;
      const pcV = pv.filter(v => v > 0);
      const pcMn = pcV.length ? Math.min(...pcV) : 0.5;
      const pcMx = pcV.length ? Math.max(...pcV) : 1.5;
      const pcR = (pcMx - pcMn) || 0.1;

      chartsRef.current.push(new Chart(mpRef.current, {
        type: 'line',
        data: {
          labels: mp.map(x => x.time),
          datasets: [
            { label: 'Spot', data: sv, borderColor: '#D1D4DC', borderWidth: 1.6, tension: 0, pointRadius: 0, yAxisID: 'y' },
            { label: 'MaxPain', data: mv, borderColor: '#FF9800', borderWidth: 1.6, borderDash: [4, 3], tension: 0, pointRadius: 0, yAxisID: 'y' },
            { label: 'PCR', data: pv, borderColor: '#26a69a', borderWidth: 1.6, tension: 0, pointRadius: 0, yAxisID: 'y1' },
          ],
        },
        options: {
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { display: false }, tooltip: ttBase() },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: Math.floor(pMn - pR * 0.05), max: Math.ceil(pMx + pR * 0.05), ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y1: { position: 'right', min: Math.max(0.5, +(pcMn - pcR * 0.1).toFixed(2)), max: Math.min(1.5, +(pcMx + pcR * 0.1).toFixed(2)), ticks: { color: tc(), font: { size: 10 }, stepSize: 0.05 }, grid: { drawOnChartArea: false } },
          },
        },
      }));
    }

    if (spotRef.current && ohlc?.labels?.length) {
      const ohlcArr = ohlc.labels.map((_, i) => ({
        o: ohlc.o[i], h: ohlc.h[i], l: ohlc.l[i], c: ohlc.c[i],
      }));
      const allP = [
        ...ohlc.h, ...ohlc.l,
        ...(ohlc.resistance || []).filter(v => v > 0),
        ...(ohlc.support || []).filter(v => v > 0),
      ];
      const mn = Math.min(...allP);
      const mx = Math.max(...allP);
      const rng = (mx - mn) || 1;

      chartsRef.current.push(new Chart(spotRef.current, {
        type: 'bar',
        data: {
          labels: ohlc.labels,
          datasets: [
            {
              type: 'bar', label: 'OHLC', order: 3,
              data: ohlc.labels.map((_, i) => {
                const top = Math.max(ohlc.o[i], ohlc.c[i]);
                const bot = Math.min(ohlc.o[i], ohlc.c[i]);
                return [bot, top];
              }),
              backgroundColor: 'rgba(0,0,0,0)',
              borderWidth: 0,
              _ohlc: ohlcArr,
            },
            {
              type: 'line', label: 'Resistance', order: 1,
              data: ohlc.resistance,
              borderColor: '#ef5350', borderWidth: 1.3, borderDash: [5, 3], tension: 0, pointRadius: 0, spanGaps: true,
            },
            {
              type: 'line', label: 'Support', order: 1,
              data: ohlc.support,
              borderColor: '#26a69a', borderWidth: 1.3, borderDash: [5, 3], tension: 0, pointRadius: 0, spanGaps: true,
            },
          ],
        },
        options: {
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...ttBase,
              callbacks: {
                title: items => items[0]?.label || '',
                label(ctx) {
                  if (ctx.datasetIndex === 0) {
                    const o = ohlcArr[ctx.dataIndex];
                    if (!o) return '';
                    return [`O: ${o.o.toLocaleString('en-IN')}`, `H: ${o.h.toLocaleString('en-IN')}`, `L: ${o.l.toLocaleString('en-IN')}`, `C: ${o.c.toLocaleString('en-IN')}`];
                  }
                  if (ctx.raw == null) return '';
                  return `${ctx.datasetIndex === 1 ? 'Res' : 'Sup'}: ${Number(ctx.raw).toLocaleString('en-IN')}`;
                },
              },
            },
          },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: Math.floor(mn - rng * 0.05), max: Math.ceil(mx + rng * 0.05), ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
          },
        },
        plugins: [candlePlugin],
      }));
    }

    if (decRef.current && dec?.length) {
      const v = [...dec.map(x => x.pe_decay), ...dec.map(x => x.ce_decay)];
      const am = Math.max(Math.abs(Math.min(...v, 0)), Math.abs(Math.max(...v, 0)));
      const sm = Math.ceil(am / 10) * 10 + 10;

      chartsRef.current.push(new Chart(decRef.current, {
        type: 'bar',
        data: {
          labels: dec.map(x => x.time),
          datasets: [
            { label: 'PE', data: dec.map(x => x.pe_decay), backgroundColor: dec.map(x => x.pe_decay >= 0 ? 'rgba(239,83,80,0.72)' : 'rgba(38,166,154,0.52)'), borderWidth: 0 },
            { label: 'CE', data: dec.map(x => x.ce_decay), backgroundColor: dec.map(x => x.ce_decay >= 0 ? 'rgba(38,166,154,0.72)' : 'rgba(239,83,80,0.52)'), borderWidth: 0 },
          ],
        },
        options: {
          plugins: { legend: { display: true, labels: { color: tc(), font: { size: 10 }, boxWidth: 8 } }, tooltip: ttBase() },
          scales: {
            x: { ticks: { color: tc(), font: { size: 9 }, maxRotation: 45 }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
            y: { min: -sm, max: sm, ticks: { color: tc(), font: { size: 10 } }, grid: { color: gc(), lineWidth: 1, tickLength: 0 } },
          },
        },
      }));
    }

    return () => {
      chartsRef.current.forEach(c => { try { c.destroy(); } catch (_) {} });
      chartsRef.current = [];
    };
  }, [series]);

  return (
    <div className="oc-ch" id="ocCharts">
      <div className="oc-ch-h">
        <i className="bi bi-graph-up" /> EOD OPTION METRICS
        <select className="days-sel" value={trendDays} onChange={e => onTrendDaysChange(Number(e.target.value))}>
          {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d}D</option>)}
        </select>
      </div>
      <div className="oc-cg">
        <div className="oc-c">
          <div className="oc-ct">OCI (TOP 6 OI) · CE OI · PE OI</div>
          <div className="oc-cv"><canvas ref={ociRef} /></div>
        </div>
        <div className="oc-c">
          <div className="oc-ct">DAILY OHLC · RESISTANCE · SUPPORT</div>
          <div className="oc-cv"><canvas ref={spotRef} /></div>
        </div>
        <div className="oc-c">
          <div className="oc-ct">MAXPAIN · SPOT · PCR</div>
          <div className="oc-cv"><canvas ref={mpRef} /></div>
        </div>
        <div className="oc-c">
          <div className="oc-ct">PREMIUM DECAY (AVG TOP 6)</div>
          <div className="oc-cv"><canvas ref={decRef} /></div>
        </div>
      </div>
    </div>
  );
}